import {
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { SvgComponent } from '@components/svg.component';
import { CourseCardComponent } from '@components/course-card/course-card.component';
import { SummaryCourseCardComponent } from '@components/summary-course-card/summary-course-card.component';
import { MealPlanModel } from '@models/meal-plan/meal-plan.model';
import { MealPlanPartModel } from '@models/meal-plan/meal-plan-part.model';
import { MealPlanService } from '@services/meal-plan.service';
import { OrderService } from '@services/order.service';
import { CourseModel } from '@models/course/course.model';
import { MenuComponentModel } from '@models/menu/menu-component.model';
import { MenuModel } from '@models/menu/menu.model';
import { OrderCartItemModel, OrderCartModel } from '@models/order/order-cart.model';
import { ServiceImageService } from '@services/service-image.service';
import { InviteOrderService } from '@services/invite-order.service';
import { CompanyService } from '@services/company.service';
import { FormatPricePipe } from '@pipes/format-price.pipe';
import { AppRoutes } from '@helpers/app-routes';

interface ServiceCourses {
  part: MealPlanPartModel;
  courses: CourseModel[];
  bgColor?: string | null;
  imageUrl?: string | null;
}

interface SelectedComponent {
  course: CourseModel;
  part: MealPlanPartModel;
  serviceIndex: number;
  bgColor: string | null;
  imageUrl: string | null;
}

@Component({
  selector: 'app-order-page',
  imports: [
    TranslateModule,
    SvgComponent,
    CourseCardComponent,
    SummaryCourseCardComponent,
    FormatPricePipe,
  ],
  templateUrl: './order-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  host: { class: 'flex h-full bg-white' },
})
export class OrderPageComponent {
  private readonly router = inject(Router);
  private readonly mealPlanService = inject(MealPlanService);
  private readonly orderService = inject(OrderService);
  private readonly serviceImageService = inject(ServiceImageService);
  private readonly inviteOrderService = inject(InviteOrderService);
  private readonly companyService = inject(CompanyService);

  readonly mealPlan = computed(() => this.mealPlanService.currentMealPlan());
  readonly isMealPlanMode = computed(() => this.mealPlan() !== null);
  readonly isFullMenu = computed(() => this.mealPlan()?.is_full_menu === true);
  readonly searchQuery = signal('');
  readonly serviceCourses = signal<ServiceCourses[]>([]);
  readonly quantities = signal<Map<number, number>>(new Map());
  readonly activeServiceId = signal<number | null>(null);

  readonly totalCoursesCount = computed(() =>
    this.serviceCourses().reduce((sum, sc) => sum + sc.courses.length, 0),
  );

  readonly activeService = computed(() => {
    const id = this.activeServiceId();
    if (id === null) return null;
    return this.serviceCourses().find((sc) => sc.part.service_id === id) ?? null;
  });

  readonly filteredServiceCourses = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    const activeId = this.activeServiceId();

    let list = this.serviceCourses();

    if (activeId !== null) {
      list = list.filter((sc) => sc.part.service_id === activeId);
    }

    if (!query) return list;

    return list
      .map((sc) => ({
        ...sc,
        courses: sc.courses.filter((c) => c.name.toLowerCase().includes(query)),
      }))
      .filter((sc) => sc.courses.length > 0);
  });

  readonly selectedItems = computed<SelectedComponent[]>(() => {
    const quantities = this.quantities();
    const allServices = this.serviceCourses();
    const result: SelectedComponent[] = [];
    for (let i = 0; i < allServices.length; i++) {
      const sc = allServices[i];
      for (const course of sc.courses) {
        if (quantities.has(course.id)) {
          result.push({
            course,
            part: sc.part,
            serviceIndex: i,
            bgColor: sc.bgColor ?? null,
            imageUrl: sc.imageUrl ?? null,
          });
        }
      }
    }
    return result;
  });

  readonly formulaPrice = computed(() => this.mealPlan()?.final_price ?? 0);

  readonly supplementTotal = computed(() => {
    const quantities = this.quantities();
    const items = this.isMealPlanMode()
      ? this.selectedItems().filter((item) => item.part.service_is_not_in_admission)
      : this.selectedItems();
    return items.reduce(
      (sum, item) => sum + (item.course.price ?? 0) * (quantities.get(item.course.id) ?? 0),
      0,
    );
  });

  readonly totalPrice = computed(() => this.formulaPrice() + this.supplementTotal());

  readonly canValidate = computed(() => {
    const quantities = this.quantities();
    if (quantities.size === 0) return false;
    if (!this.isMealPlanMode()) return true;
    const plan = this.mealPlan()!;
    const requiredParts = plan.admission_parts.filter((part) => part.is_mandatory);
    for (const part of requiredParts) {
      const sc = this.serviceCourses().find((sc) => sc.part.service_id === part.service_id);
      const total = sc?.courses.reduce((sum, c) => sum + (quantities.get(c.id) ?? 0), 0) ?? 0;
      if (total < part.quantity) return false;
    }
    if (plan.max_required) {
      const totalQuantity = this.selectedItems().reduce(
        (sum, item) => sum + (quantities.get(item.course.id) ?? 0),
        0,
      );
      if (totalQuantity < plan.admission_max) return false;
    }
    return true;
  });

  readonly isCurrentGuestFree = computed(
    () =>
      this.inviteOrderService.activeIndex() > 0 &&
      this.companyService.currentCompany()?.is_invite_payment_enabled === false,
  );

  readonly hasMoreGuestsAfterValidate = computed(() => {
    const activeIndex = this.inviteOrderService.activeIndex();
    return this.inviteOrderService
      .participants()
      .some((participant, index) => participant === null && index !== activeIndex);
  });

  readonly menu = input.required<MenuModel>();
  #initializedIndex: number | null = null;

  constructor() {
    effect(() => {
      const activeIndex = this.inviteOrderService.activeIndex();
      untracked(() => this.#init(activeIndex));
    });

    effect(() => {
      const activeIndex = this.inviteOrderService.activeIndex();
      const cart = this.#buildCart();
      const isValid = this.canValidate();
      if (activeIndex !== this.#initializedIndex) return;

      untracked(() => {
        if (cart.items.length === 0) {
          this.orderService.clear();
          return;
        }
        this.orderService.save(cart);
        this.orderService.setDraftValid(isValid);
        this.inviteOrderService.saveDraft(cart, this.mealPlan());
      });
    });
  }

  #init(activeIndex: number): void {
    this.activeServiceId.set(null);
    this.searchQuery.set('');
    this.quantities.set(new Map());

    const plan = this.mealPlan();
    if (plan) {
      this.#initMealPlanMode(plan, this.menu());
    } else {
      this.#initByCourseMode(this.menu());
    }
    this.#initializedIndex = activeIndex;
  }

  #restoreQuantities(): Map<number, number> | null {
    const order = this.orderService.currentOrder();
    if (!order || order.items.length === 0) return null;
    return new Map(order.items.map((item) => [item.course.id, item.quantity]));
  }

  #initMealPlanMode(plan: MealPlanModel, menu: MenuModel): void {
    const parts = [...plan.admission_parts].sort((a, b) => a.service_sort - b.service_sort);
    this.serviceCourses.set(
      parts.map((part) => ({
        part,
        courses: menu.components
          .filter((c: MenuComponentModel) => c.family_id === part.service_id)
          .flatMap((c: MenuComponentModel) => c.courses),
      })),
    );

    const restoredQuantities = this.#restoreQuantities();
    if (restoredQuantities) {
      this.quantities.set(restoredQuantities);
    } else if (plan.is_full_menu) {
      const allCourses = this.serviceCourses().flatMap((sc) => sc.courses);
      this.quantities.set(new Map(allCourses.map((c) => [c.id, 1])));
    }

    this.serviceImageService.getServiceImages(parts.map((p) => p.service_id)).subscribe({
      next: (images) => {
        const imageMap = new Map(images.map((img) => [img.id, img]));
        this.serviceCourses.update((list) =>
          list.map((sc) => {
            const img = imageMap.get(sc.part.service_id);
            return img ? { ...sc, bgColor: `#${img.bg}`, imageUrl: img.url } : sc;
          }),
        );
      },
    });
  }

  #initByCourseMode(menu: MenuModel): void {
    const components: MenuComponentModel[] = menu.components;
    this.serviceCourses.set(
      components.map((c, index) => ({
        part: {
          quantity: 0,
          service_id: c.family_id,
          is_mandatory: false,
          service_name: c.name,
          service_sort: index,
          service_is_not_in_admission: true,
          is_legume: false,
        },
        courses: c.courses,
      })),
    );

    const restoredQuantities = this.#restoreQuantities();
    if (restoredQuantities) {
      this.quantities.set(restoredQuantities);
    }

    this.serviceImageService.getServiceImages(components.map((c) => c.family_id)).subscribe({
      next: (images) => {
        const imageMap = new Map(images.map((img) => [img.id, img]));
        this.serviceCourses.update((list) =>
          list.map((sc) => {
            const img = imageMap.get(sc.part.service_id);
            return img ? { ...sc, bgColor: `#${img.bg}`, imageUrl: img.url } : sc;
          }),
        );
      },
    });
  }

  selectService(serviceId: number | null): void {
    this.activeServiceId.set(serviceId);
  }

  getQuantity(courseId: number): number {
    return this.quantities().get(courseId) ?? 0;
  }

  isServiceFull(part: MealPlanPartModel, courses: CourseModel[]): boolean {
    if (part.quantity === 0) return false;
    const quantities = this.quantities();
    const selected = courses.reduce((sum, c) => sum + (quantities.get(c.id) ?? 0), 0);
    return selected >= part.quantity;
  }

  increment(courseId: number): void {
    this.quantities.update((map) => {
      const next = new Map(map);
      next.set(courseId, (next.get(courseId) ?? 0) + 1);
      return next;
    });
  }

  decrement(courseId: number): void {
    this.quantities.update((map) => {
      const current = map.get(courseId) ?? 0;
      const next = new Map(map);
      if (current <= 1) next.delete(courseId);
      else next.set(courseId, current - 1);
      return next;
    });
  }

  getAllButtons() {
    return [
      {
        id: null,
        name: 'order.view_all',
        isAllButton: true,
        coursesCount: this.totalCoursesCount(),
        bgColor: null,
        imageUrl: null,
      },
      ...this.serviceCourses().map((sc) => ({
        id: sc.part.service_id,
        name: sc.part.service_name,
        isAllButton: false,
        coursesCount: sc.courses.length,
        bgColor: sc.bgColor,
        imageUrl: sc.imageUrl,
      })),
    ];
  }

  validate(): void {
    const cart = this.#buildCart();
    this.orderService.save(cart);
    this.inviteOrderService.submitCurrentCart(cart, this.mealPlan());

    const pendingIndex = this.inviteOrderService.advanceToNextPending();
    if (pendingIndex === null) {
      void this.router.navigate([AppRoutes.orderSummary]);
      return;
    }

    const draft = this.inviteOrderService.drafts()[pendingIndex];
    this.inviteOrderService.restoreParticipant(draft);
    void this.router.navigate([draft ? AppRoutes.order : AppRoutes.chooseOrderType]);
  }

  #buildCart(): OrderCartModel {
    const quantities = this.quantities();
    const seenIds = new Set<number>();
    const items: OrderCartItemModel[] = this.selectedItems()
      .filter((item) => {
        if (seenIds.has(item.course.id)) return false;
        seenIds.add(item.course.id);
        return true;
      })
      .map((item) => ({
        course: item.course,
        part: item.part,
        bgColor: item.bgColor,
        imageUrl: item.imageUrl,
        quantity: quantities.get(item.course.id) ?? 1,
      }));
    return {
      items,
      formulaPrice: this.formulaPrice(),
      supplementTotal: this.supplementTotal(),
      totalPrice: this.totalPrice(),
      isMealPlanMode: this.isMealPlanMode(),
    };
  }
}
